/**
 * Fabric samples page: filters, selection tray, modals, Cylindo (swatch code only),
 * order form submit via Shopify App Proxy (/apps/fabric-samples).
 */
(function () {
  'use strict';

  const STORAGE_PREFIX = 'comfybedssFabricSamples:';

  /* App Proxy base. Shopify forwards /apps/fabric-samples/* to the order API and
     signs the request; the server verifies the HMAC, so no form-token bootstrap. */
  const ORDER_API_BASE = '/apps/fabric-samples';

  /**
   * Maps storefront country label to ISO code for the order API.
   *
   * @param {string} raw
   * @returns {string}
   */
  function countryToIsoCode(raw) {
    const u = String(raw || '').trim().toLowerCase();
    if (u === 'united kingdom' || u === 'uk' || u === 'gb') {
      return 'GB';
    }
    return String(raw || '').trim() || 'GB';
  }

  /** @type {number|null} */
  let fabricSamplesScrollLockSavedY = null;

  /** @type {boolean} */
  let fabricSamplesBodyScrollLockedActive = false;

  /**
   * Call immediately before fabric-samples dialog.showModal(). Saves scroll position
   * and fixes the body — must run before opening so the UA cannot scroll first.
   */
  function lockFabricSamplesBodyForModal() {
    if (fabricSamplesBodyScrollLockedActive) {
      return;
    }
    fabricSamplesScrollLockSavedY = window.scrollY;
    document.documentElement.classList.add('fabric-samples-scroll-locked');
    document.body.classList.add('fabric-samples-scroll-locked');
    document.body.style.top = `-${fabricSamplesScrollLockSavedY}px`;
    fabricSamplesBodyScrollLockedActive = true;
  }

  function unlockFabricSamplesBodyForModal() {
    if (!fabricSamplesBodyScrollLockedActive) {
      return;
    }
    document.documentElement.classList.remove('fabric-samples-scroll-locked');
    document.body.classList.remove('fabric-samples-scroll-locked');
    document.body.style.removeProperty('top');
    const y = fabricSamplesScrollLockSavedY;
    fabricSamplesScrollLockSavedY = null;
    fabricSamplesBodyScrollLockedActive = false;
    if (y != null) {
      window.scrollTo(0, y);
    }
  }

  /**
   * Runs after dialogs close: unlock only when none are open (dialog stack).
   */
  function syncFabricSamplesBodyScrollLock() {
    const anyOpen =
      !!document.querySelector('.fabric-samples dialog[open]');
    if (!anyOpen) {
      unlockFabricSamplesBodyForModal();
    }
  }

  /**
   * @param {HTMLElement} viewer
   * @param {Record<string, string>} features
   */
  function setViewerFeaturesAndConfiguration(viewer, features) {
    if (!viewer) {
      return;
    }
    viewer.features = features;
    viewer.setAttribute(
      'configuration',
      Object.keys(features)
        .filter((key) => features[key] != null && features[key] !== '')
        .map((key) => `${key}:${features[key]}`)
        .join(','),
    );
  }

  /* Same path as snippets/cylindo-arc-progress-bar.liquid (background + progress). */
  const CYLINDO_ARC_PATH_D =
    'M161.438 1C161.438 0.723858 161.214 0.5 160.938 0.5C160.662 0.5 160.438 0.723858 160.438 1H160.938H161.438ZM160.938 1H160.438C160.438 3.32701 158.439 5.70012 154.411 7.96514C150.424 10.2071 144.622 12.2426 137.409 13.9582C122.991 17.3876 103.047 19.5136 80.9998 19.5136V20.0136V20.5136C103.101 20.5136 123.126 18.3833 137.64 14.9311C144.893 13.2059 150.798 11.1443 154.901 8.83677C158.964 6.55228 161.438 3.92345 161.438 1H160.938ZM80.9998 20.0136V19.5136C58.9525 19.5136 39.0086 17.3876 24.5906 13.9582C17.3776 12.2426 11.5754 10.2071 7.58855 7.96514C3.56077 5.70012 1.56152 3.32701 1.56152 1H1.06152H0.561523C0.561523 3.92345 3.03598 6.55228 7.09839 8.83677C11.2017 11.1443 17.1062 13.2059 24.3592 14.9311C38.8731 18.3833 58.8983 20.5136 80.9998 20.5136V20.0136Z';

  /**
   * SVG markup matching cylindo-arc-progress-bar.liquid with a unique clip id.
   *
   * @param {string} clipPathId
   * @returns {string}
   */
  function cylindoFabricSamplesArcSvgMarkup(clipPathId) {
    return (
      `<svg width="162" height="23" viewBox="-8 -8 178 39" fill="none" ` +
      `xmlns="http://www.w3.org/2000/svg">` +
      `<defs><clipPath id="${clipPathId}">` +
      `<rect data-fs-arc-clip-rect="" x="-8" y="-8" width="178" height="39"/>` +
      `</clipPath></defs>` +
      `<path d="${CYLINDO_ARC_PATH_D}" fill="#C8C8C3"/>` +
      `<path d="${CYLINDO_ARC_PATH_D}" fill="#FF831D" clip-path="url(#${clipPathId})"/>` +
      `<circle cx="80.9998" cy="20.0136" r="2.6667" fill="#FF831D"` +
      ` data-fs-progress-circle=""/></svg>`
    );
  }

  /**
   * Arc synced to cylindo-viewer frame-change (snippets/cylindo-viewer.liquid).
   *
   * @param {SVGSVGElement} svgEl
   * @returns {{ setProgress: (n: number) => void }}
   */
  function createFabricSamplesArcProgress(svgEl) {
    const circle = svgEl.querySelector('[data-fs-progress-circle]');
    const clipRect = svgEl.querySelector('[data-fs-arc-clip-rect]');
    if (!circle || !clipRect) {
      return { setProgress: () => {} };
    }

    /** @type {Array<{ x: number, y: number }>} */
    const points = [
      { x: 0.561523, y: 1 },
      { x: 1.56152, y: 1 },
      { x: 3.56077, y: 5.70012 },
      { x: 7.58855, y: 7.96514 },
      { x: 11.5754, y: 10.2071 },
      { x: 17.3776, y: 12.2426 },
      { x: 24.5906, y: 13.9582 },
      { x: 39.0086, y: 17.3876 },
      { x: 58.9525, y: 19.5136 },
      { x: 80.9998, y: 20.0136 },
      { x: 103.047, y: 19.5136 },
      { x: 122.991, y: 17.3876 },
      { x: 137.409, y: 13.9582 },
      { x: 144.622, y: 12.2426 },
      { x: 150.424, y: 10.2071 },
      { x: 154.411, y: 7.96514 },
      { x: 158.439, y: 5.70012 },
      { x: 160.438, y: 1 },
      { x: 160.938, y: 1 },
    ];

    let progress = 50;

    /**
     * @param {number} p
     * @returns {number}
     */
    function circleXForProgress(p) {
      const t = p / 100;
      const segmentIndex = Math.floor(t * (points.length - 1));
      const segmentProgress = (t * (points.length - 1)) - segmentIndex;
      const currentIndex = Math.min(segmentIndex, points.length - 2);
      const nextIndex = currentIndex + 1;
      const currentPoint = points[currentIndex];
      const nextPoint = points[nextIndex];
      const x =
        currentPoint.x +
        (nextPoint.x - currentPoint.x) * segmentProgress;
      const y =
        currentPoint.y +
        (nextPoint.y - currentPoint.y) * segmentProgress;
      circle.setAttribute('cx', String(x));
      circle.setAttribute('cy', String(y));
      return x;
    }

    function updateClip() {
      const circleX = circleXForProgress(progress);
      const clipStartX = -8;
      const progressWidth = circleX - clipStartX + 1;
      clipRect.setAttribute('width', String(progressWidth));
    }

    return {
      /**
       * @param {number} value
       */
      setProgress(value) {
        progress = parseFloat(String(value));
        updateClip();
      },
    };
  }

  /**
   * @param {HTMLElement} root
   * @param {{ fabrics: object[], swatches: object[], maxSwatches: number }} data
   */
  function initFabricSamples(root, data) {
    const sectionId = root.dataset.sectionId || '';
    const storageKey = `${STORAGE_PREFIX}${sectionId}`;
    const maxSwatches = Math.min(
      20,
      Math.max(1, parseInt(String(data.maxSwatches || 8), 10) || 8),
    );

    const fabricFilter = root.querySelector('[data-fabric-filter]');
    const colourFilter = root.querySelector('[data-colour-filter]');
    const badge = root.querySelector('[data-selection-badge]');
    const trayGrid = root.querySelector('[data-tray-grid]');
    const trayPanel = root.querySelector('.fabric-samples__tray');
    const orderBtn = root.querySelector('[data-order-open]');
    const jsonEl = root.querySelector('[data-fabric-samples-json]');

    const dialogAbout = root.querySelector('[data-dialog-about]');
    const dialogSwatch = root.querySelector('[data-dialog-swatch]');
    const dialogOrder = root.querySelector('[data-dialog-order]');
    const dialogSuccess = root.querySelector('[data-dialog-success]');
    const orderForm = root.querySelector('[data-order-form]');
    const orderSubmitBtn = dialogOrder?.querySelector('[data-order-submit]');

    root.querySelectorAll('dialog').forEach((dlg) => {
      dlg.addEventListener('close', () => {
        queueMicrotask(syncFabricSamplesBodyScrollLock);
      });

      dlg.addEventListener('click', (e) => {
        if (e.target === dlg) {
          dlg.close();
        }
      });
    });

    let fabricSliderResizeHandler = null;

    const DESC_PREVIEW_MAX = 175;
    const readMoreLabel = root.dataset.readMore || 'Read more';
    const readLessLabel = root.dataset.readLess || 'Read less';

    const fabricByHandle = Object.create(null);
    const fabricById = Object.create(null);
    data.fabrics.forEach((f) => {
      fabricByHandle[f.handle] = f;
      if (f.id != null && f.id !== '') {
        fabricById[String(f.id)] = f;
      }
    });
    const swatchByHandle = Object.create(null);
    data.swatches.forEach((s) => {
      swatchByHandle[s.handle] = s;
    });

    /** @type {string[]} */
    // Trim selections saved under an older, higher limit so they can't be submitted.
    let selectedHandles = loadSelection(storageKey).slice(0, maxSwatches);

    /**
     * @param {string} key
     * @returns {string[]}
     */
    function loadSelection(key) {
      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) {
          return [];
        }
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
      } catch {
        return [];
      }
    }

    /**
     * @param {string} key
     * @param {string[]} handles
     */
    function saveSelection(key, handles) {
      try {
        window.localStorage.setItem(key, JSON.stringify(handles));
      } catch {
        /* ignore quota */
      }
    }

    /**
     * @param {boolean} isLoading
     */
    function setOrderSubmitLoading(isLoading) {
      if (!orderSubmitBtn) {
        return;
      }
      orderSubmitBtn.disabled = isLoading;
      orderSubmitBtn.classList.toggle('is-loading', isLoading);
      orderSubmitBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    /**
     * Remove any previously-rendered field errors and the top-level banner.
     *
     * @param {HTMLFormElement} form
     */
    function clearOrderErrors(form) {
      form.querySelectorAll('[data-error-for]').forEach((el) => el.remove());
      form.querySelectorAll('[aria-invalid="true"]').forEach((el) => {
        el.removeAttribute('aria-invalid');
      });
      const banner = form.querySelector('[data-form-error]');
      if (banner) {
        banner.remove();
      }
    }

    /**
     * Render server-side validation errors next to the relevant fields, plus a
     * top-level banner for unkeyed problems (network, 5xx, Shopify userErrors).
     *
     * @param {HTMLFormElement} form
     * @param {Record<string, string>} errors
     */
    /**
     * Offer a paid sample pack to a household that has already had its free one.
     * Tagged data-form-error so clearOrderErrors() removes it on the next submit.
     *
     * @param {HTMLFormElement} form
     * @param {{error?: string, checkout_url: string, amount?: string}} payload
     */
    function showPaidOffer(form, payload) {
      clearOrderErrors(form);
      let url;
      try {
        url = new URL(String(payload.checkout_url));
      } catch {
        url = null;
      }
      if (!url || url.protocol !== 'https:') {
        showOrderErrors(form, { _form: payload.error || 'Order could not be placed. Please try again.' });
        return;
      }
      const box = document.createElement('div');
      box.setAttribute('data-form-error', '');
      box.setAttribute('role', 'alert');
      box.className = 'fabric-samples__paid-offer';
      const msg = document.createElement('p');
      msg.className = 'fabric-samples__paid-offer-text';
      msg.textContent = String(payload.error || 'You have already had a free sample pack.');
      const link = document.createElement('a');
      link.className = 'fabric-samples__order-btn fabric-samples__paid-offer-btn';
      link.href = url.href;
      link.textContent = payload.amount ? `Pay £${payload.amount} & order` : 'Pay & order';
      box.append(msg, link);
      form.prepend(box);
      box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    /* Back from Stripe Checkout: ?paid=1 means payment went through and the
       order is being created by the server webhook. Show the usual success modal. */
    function handleStripeReturn() {
      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch {
        return;
      }
      const paid = params.get('paid');
      if (paid === null) {
        return;
      }
      params.delete('paid');
      params.delete('session_id');
      const qs = params.toString();
      try {
        window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
      } catch {
        /* ignore */
      }
      if (paid === '1' && dialogSuccess) {
        lockFabricSamplesBodyForModal();
        dialogSuccess.showModal();
      }
    }

    function showOrderErrors(form, errors) {
      clearOrderErrors(form);
      /* Server field name -> form input name. Add entries here if create-order.js
         starts returning new keys. */
      const inputNameByErrorKey = {
        zip: 'postcode',
        address1: 'address_line_1',
        province: 'county',
      };
      let firstFocused = false;

      Object.entries(errors).forEach(([key, msg]) => {
        if (key === '_form') {
          const banner = document.createElement('div');
          banner.setAttribute('data-form-error', '');
          banner.setAttribute('role', 'alert');
          banner.className = 'fabric-samples__form-error';
          banner.textContent = String(msg);
          form.prepend(banner);
          return;
        }
        const inputName = inputNameByErrorKey[key] || key;
        const input = form.querySelector(`[name="${inputName}"]`);
        if (!input) {
          return;
        }
        input.setAttribute('aria-invalid', 'true');
        const err = document.createElement('div');
        err.setAttribute('data-error-for', inputName);
        err.className = 'fabric-samples__field-error';
        err.textContent = String(msg);
        input.parentElement?.appendChild(err);
        if (!firstFocused) {
          input.focus();
          firstFocused = true;
        }
      });
    }

    function getFabricTypeNames() {
      const set = new Set();
      data.fabrics.forEach((fabric) => {
        if (fabric.fabric_type_name) {
          set.add(fabric.fabric_type_name);
        }
      });

      /* Fallback to swatch-derived names if fabric payload is incomplete. */
      if (set.size === 0) {
        data.swatches.forEach((s) => {
          if (s.fabricTypeName) {
            set.add(s.fabricTypeName);
          }
        });
      }

      return Array.from(set).sort();
    }

    function getColourGroupNames() {
      const set = new Set();
      data.swatches.forEach((s) => {
        if (s.colourGroup) {
          set.add(s.colourGroup);
        }
      });
      return Array.from(set).sort();
    }

    function populateFilters() {
      if (fabricFilter) {
        const types = getFabricTypeNames();
        const allLabel = fabricFilter.dataset.allLabel || 'All fabrics';
        fabricFilter.innerHTML = `<option value="">${allLabel}</option>`;
        types.forEach((t) => {
          const opt = document.createElement('option');
          opt.value = t;
          opt.textContent = t;
          fabricFilter.appendChild(opt);
        });
      }
      if (colourFilter) {
        const groups = getColourGroupNames();
        const allLabel = colourFilter.dataset.allLabel || 'All colours';
        colourFilter.innerHTML = `<option value="">${allLabel}</option>`;
        groups.forEach((g) => {
          const opt = document.createElement('option');
          opt.value = g;
          opt.textContent = g;
          colourFilter.appendChild(opt);
        });
      }
    }

    function applyFilters() {
      const ft = fabricFilter ? fabricFilter.value : '';
      const cg = colourFilter ? colourFilter.value : '';
      root.querySelectorAll('[data-fabric-group]').forEach((groupEl) => {
        const fabricName = groupEl.dataset.fabricTypeName || '';
        let groupVisible = !ft || fabricName === ft;
        groupEl.querySelectorAll('[data-swatch-item]').forEach((swatchEl) => {
          const g = swatchEl.dataset.colourGroup || '';
          const show = groupVisible && (!cg || g === cg);
          swatchEl.classList.toggle('fabric-samples__hidden', !show);
        });
        const anyVisible = Array.from(
          groupEl.querySelectorAll('[data-swatch-item]'),
        ).some((el) => !el.classList.contains('fabric-samples__hidden'));
        groupEl.classList.toggle('fabric-samples__hidden', !anyVisible);
      });
    }

    function updateBadge() {
      if (!badge) {
        return;
      }
      const label = badge.dataset.badgeLabel || 'Swatches Added';
      const count = selectedHandles.length;
      const max = maxSwatches;
      const chip = `${count}/${max}`;
      const ariaTpl =
        badge.dataset.ariaTemplate || '{{ label }}, {{ count }} of {{ max }}';
      const ariaLabel = ariaTpl
        .replace(/\{\{\s*label\s*\}\}/g, label)
        .replace(/\{\{\s*count\s*\}\}/g, String(count))
        .replace(/\{\{\s*max\s*\}\}/g, String(max));
      badge.setAttribute('aria-label', ariaLabel);
      badge.innerHTML =
        '<span class="fabric-samples__badge-leading">' +
        '<span class="fabric-samples__badge-label">' +
        escapeHtml(label) +
        '</span></span>' +
        '<span class="fabric-samples__badge-chip" aria-hidden="true">' +
        escapeHtml(chip) +
        '</span>';
    }

    function updateSwatchSelectedStates() {
      root.querySelectorAll('[data-swatch-open]').forEach((btn) => {
        const h = btn.dataset.swatchHandle || '';
        btn.classList.toggle('is-selected', selectedHandles.includes(h));
      });
    }

    /* The Stripe checkout link is priced for the selection at submit time, so any
       change to the selection makes the offer stale: drop it and let the next
       submit fetch a fresh one. */
    function clearPaidOffer() {
      orderForm?.querySelectorAll('.fabric-samples__paid-offer').forEach((el) => el.remove());
    }

    function renderTray() {
      clearPaidOffer();
      if (!trayGrid) {
        return;
      }
      trayGrid.innerHTML = '';
      const removeLabel = trayGrid.dataset.removeLabel || 'Remove';

      for (let i = 0; i < maxSwatches; i += 1) {
        const slot = document.createElement('div');
        slot.className = 'fabric-samples__tray-slot';

        const handle = selectedHandles[i];
        if (handle && swatchByHandle[handle]) {
          const s = swatchByHandle[handle];
          slot.innerHTML = `
            <div class="fabric-samples__tray-slot-media fabric-samples__tray-slot-media--filled">
              <img class="fabric-samples__tray-slot-img" src="${escapeAttr(s.image)}" alt="" width="80" height="80" loading="lazy">
              <button type="button" class="fabric-samples__tray-remove" data-tray-remove="${escapeAttr(handle)}" aria-label="${escapeAttr(removeLabel)}"></button>
            </div>
            <span class="fabric-samples__tray-slot-label">${escapeHtml(s.colourName)}</span>
          `;
        } else {
          slot.classList.add('fabric-samples__tray-slot--empty');
          slot.innerHTML = `
            <div class="fabric-samples__tray-slot-media fabric-samples__tray-slot-media--empty" aria-hidden="true"></div>
            <span class="fabric-samples__tray-slot-label fabric-samples__tray-slot-label--placeholder">${i + 1}</span>
          `;
        }
        trayGrid.appendChild(slot);
      }

      trayGrid.querySelectorAll('[data-tray-remove]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const h = btn.getAttribute('data-tray-remove');
          selectedHandles = selectedHandles.filter((x) => x !== h);
          saveSelection(storageKey, selectedHandles);
          updateBadge();
          updateSwatchSelectedStates();
          renderTray();
          syncOrderButton();
        });
      });
      syncOrderButton();
    }

    function syncOrderButton() {
      if (orderBtn) {
        orderBtn.disabled = selectedHandles.length === 0;
      }
    }

    /**
     * @param {string} s
     */
    function escapeAttr(s) {
      return String(s || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
    }

    /**
     * @param {string} s
     */
    function escapeHtml(s) {
      return String(s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    /**
     * @param {{ url: string, isVideo: boolean }[]} items
     * @returns {string}
     */
    function buildFabricSliderHtml(items) {
      if (!items || items.length === 0) {
        return '';
      }
      const slides = items
        .map((item) => {
          if (item.isVideo) {
            return (
              '<div class="fabric-samples__slider-slide" data-slider-slide>' +
              `<video src="${escapeAttr(item.url)}" controls playsinline preload="metadata" draggable="false"></video></div>`
            );
          }
          return (
            '<div class="fabric-samples__slider-slide" data-slider-slide>' +
            `<img src="${escapeAttr(item.url)}" alt="" loading="lazy" width="1200" height="900" draggable="false"></div>`
          );
        })
        .join('');

      const thumbs = items
        .map((item, i) => {
          const activeCls = i === 0 ? ' is-active' : '';
          const pressed = i === 0 ? 'true' : 'false';
          const label = `Image ${i + 1} of ${items.length}`;
          if (item.isVideo) {
            return (
              `<button type="button" class="fabric-samples__slider-thumb${activeCls}" data-slider-thumb aria-label="${escapeAttr(label)}" aria-pressed="${pressed}">` +
              `<video src="${escapeAttr(item.url)}" muted playsinline preload="metadata" tabindex="-1" aria-hidden="true"></video></button>`
            );
          }
          return (
            `<button type="button" class="fabric-samples__slider-thumb${activeCls}" data-slider-thumb aria-label="${escapeAttr(label)}" aria-pressed="${pressed}">` +
            `<img src="${escapeAttr(item.url)}" alt="" width="200" height="200" loading="lazy" draggable="false"></button>`
          );
        })
        .join('');

      const singleMod = items.length < 2 ? ' fabric-samples__slider--single' : '';
      return (
        `<div class="fabric-samples__slider${singleMod}" data-fabric-slider>` +
        '<div class="fabric-samples__slider-viewport" data-slider-viewport>' +
        '<div class="fabric-samples__slider-track" data-slider-track>' +
        `${slides}</div></div>` +
        `<div class="fabric-samples__slider-thumbs">${thumbs}</div></div>`
      );
    }

    /**
     * @param {HTMLElement} root
     */
    function initFabricSlider(root) {
      if (!root) {
        return;
      }
      const track = root.querySelector('[data-slider-track]');
      const viewport = root.querySelector('[data-slider-viewport]');
      const thumbs = root.querySelectorAll('[data-slider-thumb]');
      const slideCount = root.querySelectorAll('[data-slider-slide]').length;
      if (!track || !viewport || slideCount === 0) {
        return;
      }

      if (fabricSliderResizeHandler) {
        window.removeEventListener('resize', fabricSliderResizeHandler);
        fabricSliderResizeHandler = null;
      }

      let index = 0;
      const count = slideCount;

      root.style.setProperty('--fs-slide-count', String(count));

      function widthPx() {
        return viewport.getBoundingClientRect().width;
      }

      function applyTranslate(px, withTransition) {
        track.style.transition = withTransition ? 'transform 0.35s ease' : 'none';
        track.style.transform = `translate3d(${px}px, 0, 0)`;
      }

      function syncToIndex(nextIdx, animate) {
        index = Math.max(0, Math.min(count - 1, nextIdx));
        applyTranslate(-index * widthPx(), animate);
        thumbs.forEach((btn, i) => {
          const on = i === index;
          btn.classList.toggle('is-active', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }

      function onResize() {
        if (!root.isConnected) {
          return;
        }
        applyTranslate(-index * widthPx(), false);
      }

      fabricSliderResizeHandler = onResize;
      window.addEventListener('resize', onResize);

      thumbs.forEach((btn, i) => {
        btn.addEventListener('click', () => {
          syncToIndex(i, true);
        });
      });

      let dragging = false;
      let startX = 0;
      let dragOffset = 0;

      viewport.addEventListener('pointerdown', (e) => {
        if (count < 2) {
          return;
        }
        if (e.pointerType === 'mouse' && e.button !== 0) {
          return;
        }
        dragging = true;
        startX = e.clientX;
        dragOffset = 0;
        applyTranslate(-index * widthPx(), false);
        try {
          viewport.setPointerCapture(e.pointerId);
        } catch (_e) {
          /* ignore */
        }
      });

      viewport.addEventListener('pointermove', (e) => {
        if (!dragging) {
          return;
        }
        dragOffset = e.clientX - startX;
        applyTranslate(-index * widthPx() + dragOffset, false);
      });

      function endPointer(e) {
        if (!dragging) {
          return;
        }
        dragging = false;
        try {
          if (e.pointerId != null) {
            viewport.releasePointerCapture(e.pointerId);
          }
        } catch (_e) {
          /* ignore */
        }
        const w = widthPx();
        const threshold = Math.min(72, w * 0.2);
        let next = index;
        if (dragOffset > threshold) {
          next = index - 1;
        } else if (dragOffset < -threshold) {
          next = index + 1;
        }
        dragOffset = 0;
        syncToIndex(next, true);
      }

      viewport.addEventListener('pointerup', endPointer);
      viewport.addEventListener('pointercancel', endPointer);

      syncToIndex(0, false);
    }

    /**
     * Long descriptions: first DESC_PREVIEW_MAX chars + Read more / Read less.
     *
     * @param {string} raw
     * @param {'p' | 'div'} tagName
     * @returns {string}
     */
    function buildExpandablePlainHtml(raw, tagName) {
      const text = String(raw || '');
      if (text.length <= DESC_PREVIEW_MAX) {
        return `<${tagName} class="fabric-samples-dialog__text fabric-samples-dialog__text--plain">${escapeHtml(text)}</${tagName}>`;
      }
      const head = text.slice(0, DESC_PREVIEW_MAX);
      const tail = text.slice(DESC_PREVIEW_MAX);
      return (
        `<${tagName} class="fabric-samples-dialog__text fabric-samples-dialog__text--plain" data-fs-expandable-desc>` +
        `<span data-fs-part-a>${escapeHtml(head)}</span>` +
        `<span data-fs-part-b hidden>${escapeHtml(tail)}</span>` +
        ` <button type="button" class="fabric-samples__desc-toggle" data-fs-desc-toggle aria-expanded="false">` +
        `<span data-fs-more-label>${escapeHtml(readMoreLabel)}</span>` +
        `<span data-fs-less-label hidden>${escapeHtml(readLessLabel)}</span>` +
        `</button></${tagName}>`
      );
    }

    function closeDialog(el) {
      if (el && typeof el.close === 'function') {
        el.close();
      }
      teardownCylindo(root);
    }

    /**
     * @param {HTMLElement} host
     * @param {object} swatch
     */
    function mountCylindo(host, swatch) {
      teardownCylindo(root);
      const code = (swatch.cylindoCode || '').trim();
      if (!code || !host) {
        host.innerHTML = `<p class="fabric-samples__cylindo-note">${host.dataset.emptyMsg || ''}</p>`;
        return;
      }

      host.innerHTML = '';
      const wrap = document.createElement('div');
      wrap.className = 'fabric-samples-cylindo fabric-samples__cylindo-inner';
      wrap.setAttribute('data-cylindo-show-open-close-toggle', 'true');

      const viewer = document.createElement('cylindo-viewer');
      viewer.setAttribute('customer-id', '8699');
      viewer.setAttribute('controls', '');
      viewer.setAttribute('format', 'png');
      viewer.setAttribute('code', code);

      const spin = document.createElement('cylindo-360');
      viewer.appendChild(spin);
      wrap.appendChild(viewer);

      const ctrl = document.createElement('div');
      ctrl.className = 'cylindo-controller-wrapper';

      const clipId = `fsArc-${String(sectionId).replace(/\W/g, '')}-${Date.now()}`;
      const dragSpin = document.createElement('div');
      dragSpin.className = 'drag-to-spin-img';
      dragSpin.setAttribute('aria-hidden', 'true');
      dragSpin.innerHTML = cylindoFabricSamplesArcSvgMarkup(clipId);
      ctrl.appendChild(dragSpin);

      const toggle = document.createElement('div');
      toggle.className = 'cylindo-option-toggle';
      toggle.setAttribute('role', 'button');
      toggle.setAttribute('tabindex', '0');
      toggle.textContent = 'Open & Close Storage';
      ctrl.appendChild(toggle);

      wrap.appendChild(ctrl);
      host.appendChild(wrap);

      const svgArc = dragSpin.querySelector('svg');
      const arcUi = svgArc ? createFabricSamplesArcProgress(svgArc) : null;
      arcUi?.setProgress(50);

      viewer.addEventListener('frame-change', (event) => {
        let pct = ((event.detail - 1) / (32 - 1)) * 100 + 50;
        if (pct > 100) {
          pct -= 100;
        }
        arcUi?.setProgress(pct);
      });

      const cylindoFeatures = {
        UPHOLSTERY: (swatch.colourName || '').toUpperCase(),
        'STORAGE OPTIONS': 'CLOSED',
      };

      setViewerFeaturesAndConfiguration(viewer, cylindoFeatures);

      toggle.addEventListener('click', () => {
        toggle.classList.toggle('on');
        cylindoFeatures['STORAGE OPTIONS'] = toggle.classList.contains('on')
          ? 'OPEN'
          : 'CLOSED';
        setViewerFeaturesAndConfiguration(viewer, cylindoFeatures);
      });
      toggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle.click();
        }
      });
    }

    /**
     * @param {HTMLElement} r
     */
    function teardownCylindo(r) {
      r.querySelectorAll('.fabric-samples__cylindo-inner').forEach((el) => {
        el.remove();
      });
    }

    function openAboutFabric(fabricHandle) {
      const fabric = fabricByHandle[fabricHandle];
      if (!fabric || !dialogAbout) {
        return;
      }
      const textStack = dialogAbout.querySelector('[data-dialog-about-text]');
      const mediaStack = dialogAbout.querySelector('[data-dialog-about-media]');
      const titleEl = dialogAbout.querySelector('[data-dialog-title]');
      const careHeading =
        dialogAbout.dataset.careHeading || 'Care instructions';
      const labelRub = dialogAbout.dataset.labelRub || 'Rub count';

      if (titleEl) {
        titleEl.textContent = fabric.fabric_type_name || '';
      }

      let careHtml = '';
      if (fabric.care && fabric.care.length) {
        careHtml = `<p class="fabric-samples__care-heading">${escapeHtml(careHeading)}</p><div class="fabric-samples__care-grid">`;
        fabric.care.forEach((c) => {
          careHtml += `<div class="fabric-samples__care-item"><img src="${escapeAttr(c.image)}" alt="" width="48" height="48" loading="lazy">${escapeHtml(c.text)}</div>`;
        });
        careHtml += '</div>';
      }

      const rub = fabric.rub_count ? fabric.rub_count : '';
      const comp = fabric.composition ? fabric.composition : '';
      const specHtml = `
        ${rub ? `<div class="fabric-samples__spec"><span class="fabric-samples__spec-label">${escapeHtml(labelRub)}</span><span class="fabric-samples__spec-value">${escapeHtml(rub)}</span></div>` : ''}
        ${comp ? `<div class="fabric-samples__spec fabric-samples__spec--value-only">${escapeHtml(comp)}</div>` : ''}
      `;

      const desc = fabric.fabric_description || '';
      const descHtml = desc
        ? buildExpandablePlainHtml(desc, 'div')
        : '';

      if (textStack) {
        textStack.innerHTML = `${descHtml}${careHtml}${specHtml}`;
      }

      const hasAboutMedia = !!(fabric.slider && fabric.slider.length);

      if (mediaStack) {
        if (!hasAboutMedia) {
          if (fabricSliderResizeHandler) {
            window.removeEventListener('resize', fabricSliderResizeHandler);
            fabricSliderResizeHandler = null;
          }
          mediaStack.innerHTML = '';
        } else {
          mediaStack.innerHTML = buildFabricSliderHtml(fabric.slider);
          const sliderRoot = mediaStack.querySelector('[data-fabric-slider]');
          if (sliderRoot) {
            initFabricSlider(sliderRoot);
          }
        }
      }

      const aboutBody = dialogAbout.querySelector(
        '.fabric-samples-dialog__body--about',
      );
      const aboutColMedia = mediaStack?.closest(
        '.fabric-samples-dialog__col-media',
      );
      if (aboutBody) {
        aboutBody.classList.toggle(
          'fabric-samples-dialog__body--about-no-media',
          !hasAboutMedia,
        );
      }
      if (aboutColMedia) {
        aboutColMedia.toggleAttribute('hidden', !hasAboutMedia);
      }

      dialogAbout.classList.toggle(
        'fabric-samples-dialog--about-compact',
        !hasAboutMedia,
      );

      lockFabricSamplesBodyForModal();
      dialogAbout.showModal();
    }

    function openSwatchDetail(swatchHandle) {
      const swatch = swatchByHandle[swatchHandle];
      if (!swatch || !dialogSwatch) {
        return;
      }
      const fabric =
        (swatch.fabricHandle && fabricByHandle[swatch.fabricHandle]) ||
        (swatch.fabricId != null &&
          swatch.fabricId !== '' &&
          fabricById[String(swatch.fabricId)]) ||
        null;
      const textStack = dialogSwatch.querySelector('[data-dialog-text]');
      const cylindoHost = dialogSwatch.querySelector('[data-cylindo-host]');
      const titleEl = dialogSwatch.querySelector('[data-dialog-title]');
      const addLabel = dialogSwatch.dataset.addLabel || 'Add to swatches';
      const labelRub = dialogSwatch.dataset.labelRub || 'Rub count';
      const careHeading =
        dialogSwatch.dataset.careHeading || 'Care instructions';

      if (titleEl) {
        titleEl.textContent = swatch.colourName || '';
      }

      let careHtml = '';
      if (fabric && fabric.care && fabric.care.length) {
        careHtml = `<p class="fabric-samples__care-heading">${escapeHtml(careHeading)}</p><div class="fabric-samples__care-grid">`;
        fabric.care.forEach((c) => {
          careHtml += `<div class="fabric-samples__care-item"><img src="${escapeAttr(c.image)}" alt="" width="48" height="48" loading="lazy">${escapeHtml(c.text)}</div>`;
        });
        careHtml += '</div>';
      }

      const rub = fabric && fabric.rub_count ? fabric.rub_count : '';
      const comp = fabric && fabric.composition ? fabric.composition : '';
      const specHtml = `
        ${rub ? `<div class="fabric-samples__spec"><span class="fabric-samples__spec-label">${escapeHtml(labelRub)}</span><span class="fabric-samples__spec-value">${escapeHtml(rub)}</span></div>` : ''}
        ${comp ? `<div class="fabric-samples__spec fabric-samples__spec--value-only">${escapeHtml(comp)}</div>` : ''}
      `;

      const desc = swatch.description || '';
      const atMax = selectedHandles.length >= maxSwatches;
      const already = selectedHandles.includes(swatchHandle);
      const addDisabled = atMax || already;

      if (textStack) {
        textStack.innerHTML = `${buildExpandablePlainHtml(desc, 'div')}${careHtml}${specHtml}`;
      }

      dialogSwatch.querySelectorAll('[data-dialog-swatch-add]').forEach((btn) => {
        btn.textContent = addLabel;
        btn.hidden = false;
        btn.setAttribute('data-add-swatch', swatchHandle);
        btn.disabled = addDisabled;
      });
      dialogSwatch
        .querySelectorAll('[data-dialog-swatch-delivery]')
        .forEach((el) => {
          el.hidden = false;
        });

      if (cylindoHost) {
        cylindoHost.dataset.emptyMsg = dialogSwatch.dataset.cylindoEmpty || '';
        mountCylindo(cylindoHost, swatch);
      }

      lockFabricSamplesBodyForModal();
      dialogSwatch.showModal();
    }

    root.addEventListener('click', (e) => {
      const descToggle = e.target.closest('[data-fs-desc-toggle]');
      if (descToggle) {
        const wrap = descToggle.closest('[data-fs-expandable-desc]');
        if (wrap) {
          const partB = wrap.querySelector('[data-fs-part-b]');
          const moreLbl = descToggle.querySelector('[data-fs-more-label]');
          const lessLbl = descToggle.querySelector('[data-fs-less-label]');
          if (partB && moreLbl && lessLbl) {
            const expanded = descToggle.getAttribute('aria-expanded') === 'true';
            if (expanded) {
              partB.hidden = true;
              descToggle.setAttribute('aria-expanded', 'false');
              moreLbl.hidden = false;
              lessLbl.hidden = true;
            } else {
              partB.hidden = false;
              descToggle.setAttribute('aria-expanded', 'true');
              moreLbl.hidden = true;
              lessLbl.hidden = false;
            }
          }
        }
        return;
      }
      const aboutBtn = e.target.closest('[data-about-fabric]');
      if (aboutBtn) {
        const fh = aboutBtn.getAttribute('data-fabric-handle');
        if (fh) {
          openAboutFabric(fh);
        }
        return;
      }
      const swBtn = e.target.closest('[data-swatch-open]');
      if (swBtn) {
        const sh = swBtn.getAttribute('data-swatch-handle');
        if (sh) {
          openSwatchDetail(sh);
        }
      }
    });

    root.querySelectorAll('[data-dialog-close]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const d = btn.closest('dialog');
        closeDialog(d);
      });
    });

    dialogAbout?.addEventListener('close', () => {
      if (fabricSliderResizeHandler) {
        window.removeEventListener('resize', fabricSliderResizeHandler);
        fabricSliderResizeHandler = null;
      }
    });

    dialogSwatch?.addEventListener('close', () => {
      teardownCylindo(root);
      dialogSwatch.querySelectorAll('[data-dialog-swatch-add]').forEach((btn) => {
        btn.hidden = true;
        btn.removeAttribute('data-add-swatch');
      });
      dialogSwatch
        .querySelectorAll('[data-dialog-swatch-delivery]')
        .forEach((el) => {
          el.hidden = true;
        });
    });
    dialogSwatch?.addEventListener('cancel', () => {
      teardownCylindo(root);
    });

    dialogSwatch?.addEventListener('click', (e) => {
      const add = e.target.closest('[data-add-swatch]');
      if (!add || add.disabled) {
        return;
      }
      const sh = add.getAttribute('data-add-swatch');
      if (!sh || selectedHandles.includes(sh) || selectedHandles.length >= maxSwatches) {
        return;
      }
      selectedHandles.push(sh);
      saveSelection(storageKey, selectedHandles);
      updateBadge();
      updateSwatchSelectedStates();
      renderTray();
      closeDialog(dialogSwatch);
    });

    orderBtn?.addEventListener('click', () => {
      if (selectedHandles.length === 0) {
        return;
      }
      lockFabricSamplesBodyForModal();
      dialogOrder?.showModal();
    });

    orderForm?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!orderForm) {
        return;
      }

      if (orderSubmitBtn?.classList.contains('is-loading')) {
        return;
      }

      clearOrderErrors(orderForm);

      const fd = new FormData(orderForm);
      const first = String(fd.get('first_name') || '').trim();
      const last = String(fd.get('last_name') || '').trim();
      const email = String(fd.get('email') || '').trim();
      const phone = String(fd.get('phone') || '').trim();
      const address1 = String(fd.get('address_line_1') || '').trim();
      const address2 = String(fd.get('address_line_2') || '').trim();
      const city = String(fd.get('city') || '').trim();
      const province = String(fd.get('county') || '').trim();
      const zip = String(fd.get('postcode') || '').trim();
      const countryRaw = String(fd.get('country') || '');
      const country = countryToIsoCode(countryRaw);
      const note = String(fd.get('order_notes') || '').trim();

      /** @type {{ variant_id: number, name: string, colour: string, collection: string }[]} */
      const swatches = [];
      for (let i = 0; i < selectedHandles.length; i += 1) {
        const h = selectedHandles[i];
        const s = swatchByHandle[h];
        if (!s) {
          continue;
        }
        const vid = s.variantId;
        let variant_id = NaN;
        if (typeof vid === 'number' && Number.isFinite(vid)) {
          variant_id = vid;
        } else if (vid != null && String(vid).trim() !== '') {
          variant_id = parseInt(String(vid).trim(), 10);
        }
        if (!Number.isFinite(variant_id) || variant_id <= 0) {
          continue;
        }
        const fabricName = String(s.fabricTypeName || '').trim();
        swatches.push({
          variant_id,
          name: fabricName,
          colour: String(s.colourName || '').trim(),
          collection: fabricName,
        });
      }

      if (swatches.length === 0) {
        showOrderErrors(orderForm, { _form: 'Please select at least one swatch before ordering.' });
        return;
      }

      const body = {
        swatches,
        customer: {
          first_name: first,
          last_name: last,
          email,
          phone,
        },
        address: {
          address1,
          address2,
          city,
          province,
          zip,
          country,
          company: '',
        },
        note,
      };

      const postUrl = `${ORDER_API_BASE}/api/create-order`;

      setOrderSubmitLoading(true);
      try {
        const res = await fetch(postUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        });
        const payload = await res.json().catch(() => ({}));

        /* Repeat household: the free pack has been used, so the server offers a
           paid pack (Stripe Checkout) instead of a hard block. */
        if (res.status === 402 && payload.code === 'paid_sample_required' && payload.checkout_url) {
          showPaidOffer(orderForm, payload);
          return;
        }

        if (!res.ok) {
          showOrderErrors(
            orderForm,
            payload.errors || {
              _form: payload.error || 'Order could not be placed. Please try again.',
            },
          );
          return;
        }

        closeDialog(dialogOrder);
        lockFabricSamplesBodyForModal();
        dialogSuccess?.showModal();
        orderForm.reset();
      } catch (_err) {
        showOrderErrors(orderForm, { _form: 'Network error. Please try again.' });
      } finally {
        setOrderSubmitLoading(false);
      }
    });

    fabricFilter?.addEventListener('change', applyFilters);
    colourFilter?.addEventListener('change', applyFilters);

    if (badge && trayPanel) {
      const BADGE_SCROLL_OFFSET = 236;
      badge.addEventListener('click', () => {
        const top =
          trayPanel.getBoundingClientRect().top +
          window.scrollY -
          BADGE_SCROLL_OFFSET;
        window.scrollTo({
          behavior: 'smooth',
          top: Math.max(0, top),
        });
      });
    }

    populateFilters();
    applyFilters();
    updateBadge();
    updateSwatchSelectedStates();
    renderTray();
    handleStripeReturn();

    /* Back button from Stripe restores this page from bfcache with the old offer
       still in the form; clear it so a resubmit prices the current selection. */
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        clearPaidOffer();
      }
    });
  }

  function boot() {
    document.querySelectorAll('[data-fabric-samples]').forEach((root) => {
      const jsonEl = root.querySelector('[data-fabric-samples-json]');
      if (!jsonEl || !jsonEl.textContent) {
        return;
      }
      try {
        const data = JSON.parse(jsonEl.textContent.trim());
        initFabricSamples(root, data);
      } catch {
        /* ignore */
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
